<template>
    <h1 class="control-panel-title">Actieve groepen en sessies</h1>
    <div class="control-panel-content container">
        <div v-for="group in groupedOngoingSessions" :key="group.groupName + '-ongoing'" class="row">
            <div class="group-header">
                <h3>{{ group.groupName || '(Geen groep)' }}</h3>
            </div>
            <p>
                <small>{{ formatDate(group.createdAt) }}</small>
                &nbsp;&nbsp;&nbsp;&nbsp;
                <button v-if="hasPaused(group)" class="btn-group-action" @click="resumeGroup(group)">
                    <font-awesome-icon icon="fa-solid fa-play" /> Hervatten
                </button>
                <button v-if="hasUnpaused(group)" class="btn-group-action" @click="pauseGroup(group)">
                    <font-awesome-icon icon="fa-solid fa-pause" /> Pauzeren
                </button>
                <button class="btn-group-action" @click="stopGroup(group)">
                    <font-awesome-icon icon="fa-solid fa-stop" /> Stoppen
                </button>
                <button class="btn-group-action btn-danger" @click="deleteGroup(group)">
                    <font-awesome-icon icon="fa-solid fa-trash" /> Verwijderen
                </button>
            </p>
            <table class="table custom-table">
                <thead>
                    <tr>
                        <th width="50%">E-mail</th>
                        <th width="50%">Sessiecode</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="session in group.sessions" :key="session.id">
                        <td>
                            <router-link :to="'/admin/sessions/' + session.adminCode">{{ session.email }}</router-link>
                        </td>
                        <td>
                            {{ session.adminCode }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <p v-if="groupedOngoingSessions.length === 0">Geen actieve sessies.</p>
    </div>

    <h1 class="control-panel-title">Afgeronde sessies</h1>
    <div class="control-panel-content container">
        <div v-for="group in groupedFinishedSessions" :key="group.groupName + '-finished'" class="row">
            <h3>{{ group.groupName || '(Geen groep)' }}</h3>
            <p>
                <small>{{ formatDate(group.createdAt) }}</small>
                &nbsp;&nbsp;&nbsp;&nbsp;
                <button class="btn-group-action btn-danger" @click="deleteGroup(group)">
                    <font-awesome-icon icon="fa-solid fa-trash" /> Verwijderen
                </button>
            </p>
            <table class="table custom-table">
                <thead>
                    <tr>
                        <th width="50%">E-mail</th>
                        <th width="50%">Sessiecode</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="session in group.sessions" :key="session.id">
                        <td>
                            <router-link :to="'/admin/sessions/' + session.adminCode">{{ session.email }}</router-link>
                        </td>
                        <td>
                            {{ session.adminCode }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <p v-if="groupedFinishedSessions.length === 0">Geen afgeronde sessies.</p>
    </div>
</template>

<script>
import api from '@/repository/api.js'

export default {
    data() {
        return {
            sessions: [],
        };
    },
    computed: {
        groupedOngoingSessions() {
            const ongoingSessions = this.sessions.filter(session => !this.isFinished(session));
            return this.groupSessions(ongoingSessions);
        },
        groupedFinishedSessions() {
            const finishedSessions = this.sessions.filter(session => this.isFinished(session));
            return this.groupSessions(finishedSessions);
        }
    },
    async created() {
        this.fetchSessions();
    },
    methods: {
        groupSessions(sessions) {
            const groups = {};
            for (const session of sessions) {
                const key = session.groupName || '';
                if (!groups[key]) {
                    groups[key] = {
                        groupName: key,
                        createdAt: session.createdAt,
                        sessions: [],
                    };
                } else if (session.createdAt < groups[key].createdAt) {
                    groups[key].createdAt = session.createdAt;
                }
                groups[key].sessions.push(session);
            }
            const result = Object.values(groups);
            result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            for (const group of result) {
                group.sessions.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            }
            return result;
        },
        isFinished(session) {
            if (session.endedAt) {
                return true;
            }
            if (!session.totalSteps) {
                return false;
            }
            return session.progress >= session.totalSteps;
        },
        hasPaused(group) {
            return group.sessions.some(session => session.isPaused);
        },
        hasUnpaused(group) {
            return group.sessions.some(session => !session.isPaused);
        },
        async pauseGroup(group) {
            const token = this.$store.state.sessionToken;
            const sessions = group.sessions.filter(session => !session.isPaused);
            await Promise.allSettled(sessions.map(session => api.pauseGame(token, session.adminCode)));
            await this.fetchSessions();
        },
        async resumeGroup(group) {
            const token = this.$store.state.sessionToken;
            const sessions = group.sessions.filter(session => session.isPaused);
            await Promise.allSettled(sessions.map(session => api.resumeGame(token, session.adminCode, session.progress, false)));
            await this.fetchSessions();
        },
        groupLabel(group) {
            return group.groupName || '(Geen groep)';
        },
        async stopGroup(group) {
            if (!confirm(`Weet je zeker dat je alle sessies van groep ${this.groupLabel(group)} wilt stoppen? Het spel kan daarna niet meer worden hervat.`)) {
                return;
            }
            const token = this.$store.state.sessionToken;
            await Promise.allSettled(group.sessions.map(session => api.stopGame(token, session.adminCode)));
            await this.fetchSessions();
        },
        async deleteGroup(group) {
            if (!confirm(`Weet je zeker dat je groep ${this.groupLabel(group)} en alle ${group.sessions.length} sessie(s) inclusief logs wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`)) {
                return;
            }
            const token = this.$store.state.sessionToken;
            await Promise.allSettled(group.sessions.map(session => api.deleteSession(token, session.adminCode)));
            await this.fetchSessions();
        },
        async fetchSessions() {
            try {
                const token = this.$store.state.sessionToken;
                const response = await api.fetchAllSessions(token);
                this.sessions = response.data.data;
            } catch (error) {
                console.error("An error occurred while fetching data: ", error);
            }
        },
        formatDate(timestamp) {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const date = new Date(timestamp);

            const day = date.getDate();
            const month = months[date.getMonth()];
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');

            return `${day} ${month} ${year}, ${hours}:${minutes}`;
        }

    }
}
</script>

<style scoped>
.custom-table {
    width: 100%;
    border-collapse: collapse;
}

.custom-table th,
.custom-table td {
    text-align: left;
    padding: 8px;
}

tbody tr:nth-child(odd) {
    background-color: white;
}
.row {
    margin-bottom: 2rem;
}
.control-panel-content {
    margin-bottom: 2rem;
}
.group-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}
.group-header h3 {
    margin: 0;
}
.group-actions {
    display: flex;
    gap: 0.5rem;
}
.btn-group-action {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    font-size: 1rem;
    font-weight: 700;
    color: white;
    background: var(--color-primary);
    border: none;
    border-radius: var(--button-border-radius);
    box-shadow: var(--shadow);
    cursor: pointer;
}
.btn-group-action + .btn-group-action {
    margin-left: 0.5rem;
}
.btn-group-action.btn-danger {
    background: #dc3545;
}
.btn-group-action svg {
    width: 1rem;
    height: 1rem;
}
</style>
